/// <reference types="@types/google.maps" />
import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import { useEffect, useRef, useState } from "react";
import { FORM_GROUP_STYLE, LABEL_STYLE } from "../../styles/modalStyles";

export interface LocationResult {
  address: string;
  latitude: number;
  longitude: number;
}

interface LocationAutocompleteFieldProps {
  label: string;
  onChange: (result: LocationResult) => void;
  initialValue?: string;
  required?: boolean;
  error?: string;
}

setOptions({
  key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string,
  v: "beta",
});

const INPUT_STYLE: React.CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  fontSize: "14px",
  fontFamily: "inherit",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  boxSizing: "border-box",
  outline: "none",
  color: "#111827",
  backgroundColor: "#fff",
  cursor: "pointer",
};

export function LocationAutocompleteField({
  label,
  onChange,
  initialValue,
  required = false,
  error,
}: LocationAutocompleteFieldProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLElement | null>(null);
  const onChangeRef = useRef<(result: LocationResult) => void>(onChange);
  const [editing, setEditing] = useState(!initialValue);
  const [currentAddress, setCurrentAddress] = useState(initialValue ?? "");

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!editing) return;
    let cancelled = false;

    importLibrary("places")
      .then((places) => {
        if (cancelled || !containerRef.current) return;

        const autocomplete = new places.PlaceAutocompleteElement({
          types: ["address"],
        } as google.maps.places.PlaceAutocompleteElementOptions);
        autocomplete.style.cssText =
          "width: 100%; font-size: 14px; font-family: inherit;";
        elementRef.current = autocomplete as unknown as HTMLElement;
        containerRef.current.appendChild(
          autocomplete as unknown as HTMLElement,
        );

        autocomplete.addEventListener("gmp-select", async (event: Event) => {
          const ev = event as Event & Record<string, unknown>;
          const placePrediction = ev["placePrediction"] as
            | { toPlace?: () => google.maps.places.Place }
            | null
            | undefined;
          if (!placePrediction) return;
          const place = placePrediction.toPlace?.();
          if (!place) return;
          try {
            await place.fetchFields({
              fields: ["formattedAddress", "location"],
            });
          } catch (err: unknown) {
            console.error("[LocationAutocomplete] fetchFields failed:", err);
            return;
          }
          const address = place.formattedAddress ?? "";
          const latitude = place.location?.lat() ?? 0;
          const longitude = place.location?.lng() ?? 0;
          setCurrentAddress(address);
          setEditing(false);
          onChangeRef.current({ address, latitude, longitude });
        });
      })
      .catch((err: unknown) => {
        if (!cancelled)
          console.error("[LocationAutocomplete] Failed to load Places:", err);
      });

    return () => {
      cancelled = true;
      if (
        elementRef.current &&
        containerRef.current?.contains(elementRef.current)
      ) {
        containerRef.current.removeChild(elementRef.current);
      }
      elementRef.current = null;
    };
  }, [editing]);

  return (
    <div style={FORM_GROUP_STYLE}>
      <label style={LABEL_STYLE}>
        {label}
        {required && (
          <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
        )}
      </label>

      {editing ? (
        <>
          <div ref={containerRef} style={{ width: "100%" }} />
          {currentAddress && (
            <button
              type="button"
              onClick={() => setEditing(false)}
              style={{
                marginTop: "6px",
                fontSize: "12px",
                color: "#6b7280",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                textDecoration: "underline",
              }}
            >
              Cancel
            </button>
          )}
        </>
      ) : (
        <input
          style={INPUT_STYLE}
          value={currentAddress}
          readOnly
          onClick={() => setEditing(true)}
          title="Click to change address"
        />
      )}

      {error && (
        <span
          style={{
            display: "block",
            marginTop: "4px",
            fontSize: "12px",
            color: "#ef4444",
          }}
        >
          {error}
        </span>
      )}
      <span
        style={{
          display: "block",
          marginTop: "4px",
          fontSize: "12px",
          color: "#6b7280",
        }}
      >
        {editing
          ? "Select from the suggestions to set GPS coordinates"
          : "Click to change the address"}
      </span>
    </div>
  );
}
